/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FaStar, FaRegStar, FaStarHalfAlt } from 'react-icons/fa';
import { FiCheckCircle } from 'react-icons/fi';
import PackageImage from '../common/PackageImage';
import { PackageLightbox } from './PackageGallery';
import { formatIsoDate } from '../../utils/packageUtils';

const SHOWN = 4;

/** Five stars for a 0–5 rating, rounded to the nearest half. */
function Stars({ value, size }) {
  const v = Math.max(0, Math.min(5, Math.round((Number(value) || 0) * 2) / 2));
  return (
    <span className="pkg-stars" role="img" aria-label={`${v} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        if (v >= n) return <FaStar key={n} size={size} />;
        if (v >= n - 0.5) return <FaStarHalfAlt key={n} size={size} />;
        return <FaRegStar key={n} size={size} className="pkg-stars__empty" />;
      })}
    </span>
  );
}

Stars.propTypes = {
  value: PropTypes.number,
  size: PropTypes.number,
};

Stars.defaultProps = {
  value: 0,
  size: 15,
};

/** Rating summary with a bar per star, then the reviews themselves. */
function PackageReviews({ reviews }) {
  const [showAll, setShowAll] = useState(false);
  const [photos, setPhotos] = useState(null); // { images, index }
  const max = Math.max(1, ...reviews.breakdown.map((b) => b.count));
  const list = showAll ? reviews.list : reviews.list.slice(0, SHOWN);

  return (
    <div className="pkg-reviews">
      <div className="pkg-reviews__summary">
        <div className="pkg-reviews__score">
          <strong>
            {reviews.average != null ? Number(reviews.average).toFixed(1) : '—'}
          </strong>
          <Stars value={reviews.average} size={18} />
          <small>
            {reviews.count} review{reviews.count === 1 ? '' : 's'}
          </small>
        </div>
        <ul className="pkg-reviews__bars">
          {reviews.breakdown.map((b) => (
            <li key={b.star}>
              <span className="pkg-reviews__bar-star">
                {b.star} <FaStar size={11} />
              </span>
              <span className="pkg-reviews__bar-track" aria-hidden>
                <span style={{ width: `${(b.count / max) * 100}%` }} />
              </span>
              <span className="pkg-reviews__bar-count">{b.count}</span>
            </li>
          ))}
        </ul>
      </div>

      {list.length > 0 && (
        <ul className="pkg-reviews__list">
          {list.map((r) => (
            <li key={r.id} className="pkg-review">
              <div className="pkg-review__head">
                <strong>{r.name || 'Traveller'}</strong>
                {r.date && <time>{formatIsoDate(r.date)}</time>}
              </div>
              <div className="pkg-review__meta">
                {r.rating != null && <Stars value={r.rating} size={13} />}
                {r.verified && (
                  <span className="pkg-review__verified">
                    <FiCheckCircle /> Verified traveller
                  </span>
                )}
              </div>
              {r.title && <p className="pkg-review__title">{r.title}</p>}
              {r.text && <p className="pkg-review__text">{r.text}</p>}
              {r.images.length > 0 && (
                <div className="pkg-review__photos">
                  {r.images.map((src, i) => (
                    <button
                      type="button"
                      key={src}
                      onClick={() =>
                        setPhotos({
                          images: r.images.map((url) => ({ url, caption: '' })),
                          index: i,
                        })
                      }
                      aria-label={`Photo ${i + 1} by ${r.name || 'traveller'}`}
                    >
                      <PackageImage src={src} />
                    </button>
                  ))}
                </div>
              )}
              {r.reply && (
                <p className="pkg-review__reply">
                  <strong>Reply from the supplier:</strong> {r.reply}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {reviews.list.length > SHOWN && (
        <button
          type="button"
          className="pkg-reviews__more"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll
            ? 'Show fewer reviews'
            : `Show all ${reviews.list.length} reviews`}
        </button>
      )}

      {photos && (
        <PackageLightbox
          images={photos.images}
          startIndex={photos.index}
          title="Traveller photos"
          onClose={() => setPhotos(null)}
        />
      )}
    </div>
  );
}

PackageReviews.propTypes = {
  reviews: PropTypes.object.isRequired,
};

export default PackageReviews;
